import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import wsfeConfigService, { WsfeCertificadoInfo } from '../../services/wsfeConfigService';
import { isAdminRole } from '../../utils/ubicacionUsuarioRoles';
import {
  certRequiereAviso,
  DIAS_AVISO_POR_VENCER,
  formatFechaCertificado,
  labelVigenciaCert,
} from '../../utils/wsfeCertificadoVigencia';

interface CertificadoArcaVencimientoAlertProps {
  sx?: object;
}

function tieneCertificadoRuta(ruta?: string | null): boolean {
  return Boolean((ruta ?? '').trim());
}

const CertificadoArcaVencimientoAlert: React.FC<CertificadoArcaVencimientoAlertProps> = ({ sx }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const esAdmin = useMemo(() => isAdminRole(user?.roles), [user?.roles]);
  const [avisos, setAvisos] = useState<WsfeCertificadoInfo[]>([]);

  useEffect(() => {
    if (!esAdmin) {
      setAvisos([]);
      return;
    }

    let cancelled = false;

    const cargar = async () => {
      try {
        const configs = await wsfeConfigService.getWsfeConfigs();
        const conCert = configs.filter(c => tieneCertificadoRuta(c.certificado));
        if (conCert.length === 0) {
          if (!cancelled) setAvisos([]);
          return;
        }

        const results = await Promise.allSettled(
          conCert.map(c => wsfeConfigService.getCertificadoInfo(c.idwsfe))
        );

        if (cancelled) return;

        const conAviso = results
          .filter((r): r is PromiseFulfilledResult<WsfeCertificadoInfo> => r.status === 'fulfilled')
          .map(r => r.value)
          .filter(info => certRequiereAviso(info, DIAS_AVISO_POR_VENCER));

        setAvisos(conAviso);
      } catch {
        if (!cancelled) setAvisos([]);
      }
    };

    void cargar();

    return () => {
      cancelled = true;
    };
  }, [esAdmin]);

  if (!esAdmin || avisos.length === 0) return null;

  const hayVencidos = avisos.some(info => info.vencido);

  return (
    <Alert
      severity={hayVencidos ? 'error' : 'warning'}
      sx={{ mb: 2, borderRadius: 2, ...sx }}
      action={
        <Button
          color="inherit"
          size="small"
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          onClick={() => navigate('/sistema/companias')}
        >
          Ir a Compañías
        </Button>
      }
    >
      {hayVencidos
        ? 'Hay certificados ARCA vencidos. Renoválos para evitar fallas al autorizar comprobantes.'
        : `Hay certificados ARCA que vencen dentro de los próximos ${DIAS_AVISO_POR_VENCER} días.`}
      <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5, mb: 0 }}>
        {avisos.map(info => (
          <li key={info.idwsfe}>
            <Typography variant="body2" component="span">
              {info.idwsfe}: {labelVigenciaCert(info)}
              {formatFechaCertificado(info.validoHasta)
                ? ` (${formatFechaCertificado(info.validoHasta)})`
                : ''}
            </Typography>
          </li>
        ))}
      </Box>
    </Alert>
  );
};

export default CertificadoArcaVencimientoAlert;
